import { useEffect, type ReactNode } from 'react';

import { Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { EmptyState } from '@/ui/EmptyState';

import { usePortalAccess } from './usePortalAccess';

/**
 * Guarda de tela do Portal do Transportador, igual à do protótipo
 * (`PortalAccessGuard.tsx`):
 * - `requirePJ`: Condutores (PJ ou Cooperativa);
 * - `requireCoop`: Cooperados e Inscrever Terceiros (Cooperativa);
 * - `requireAdminCred`: Minha Equipe (quem administra o credenciado coletivo,
 *   ver `usePortalAccess`).
 *
 * No protótipo a administração interna passava pelas três; aqui ela nem chega
 * ao Portal (`AREA_CREDENCIADO.papeis`), e por isso nenhuma regra a libera.
 * PF barrado em `requireAdminCred` vê o texto de `requirePJ`: a tela não é
 * dele por não ter equipe, não por falta de papel de administrador.
 * Tela que não declara regra é de todos os credenciados.
 *
 * Quem não passa vê sempre a mesma tela bloqueada, com o texto da regra e o
 * caminho de volta, e um aviso curto para quem chegou por link compartilhado.
 */
export type RegraDeAcessoDoPortal = 'requirePJ' | 'requireCoop' | 'requireAdminCred';

const TEXTO: Record<RegraDeAcessoDoPortal, { title: string; description: string; toast: string }> =
  {
    requirePJ: {
      title: 'Tela exclusiva de PJ e Cooperativas',
      description:
        'Esta seção é destinada a credenciados Pessoa Jurídica ou Cooperativas, que gerenciam equipe de condutores própria. No seu perfil (Pessoa Física), você é o próprio condutor da sua frota.',
      toast: 'Acesso restrito: tela exclusiva de PJ/Cooperativa.',
    },
    requireCoop: {
      title: 'Tela exclusiva de Cooperativas',
      description:
        'Esta seção é destinada a credenciados do tipo Cooperativa, para gestão de cooperados e inscrição de veículos de terceiros.',
      toast: 'Acesso restrito: tela exclusiva de Cooperativas.',
    },
    requireAdminCred: {
      title: 'Acesso restrito a administradores',
      description:
        'Apenas o administrador do credenciado pode gerenciar a equipe de colaboradores e seus perfis de acesso ao portal.',
      toast: 'Acesso restrito: apenas administradores do credenciado.',
    },
  };

interface PortalAccessGuardProps {
  regra: RegraDeAcessoDoPortal;
  children: ReactNode;
}

export function PortalAccessGuard({ regra, children }: PortalAccessGuardProps) {
  const acesso = usePortalAccess();

  const permitido =
    (regra === 'requirePJ' && acesso.isColetivo) ||
    (regra === 'requireCoop' && acesso.isCoop) ||
    (regra === 'requireAdminCred' && acesso.isAdminCred);

  if (permitido) return <>{children}</>;
  return (
    <TelaBloqueada
      regra={regra === 'requireAdminCred' && !acesso.isColetivo ? 'requirePJ' : regra}
    />
  );
}

function TelaBloqueada({ regra }: { regra: RegraDeAcessoDoPortal }) {
  const navigate = useNavigate();
  const texto = TEXTO[regra];

  useEffect(() => {
    toast.warning(texto.toast);
  }, [texto.toast]);

  return (
    <div className="py-8">
      <EmptyState
        variant="error"
        icon={Lock}
        title={texto.title}
        description={texto.description}
        actionLabel="Voltar para o Início"
        onAction={() => navigate('/credenciado')}
      />
    </div>
  );
}
